// "Unidades" de la carga masiva: cada unidad es UN producto a crear, con 1+
// imágenes. Es la única fuente de verdad para lo que se muestra en la lista
// del modal "Subir varios productos" Y para las tareas que arma `saveAll` —
// si se calcularan por separado, lo que ves y lo que se crea divergirían en
// el primer cambio.
//
// La base de agrupamiento depende del toggle "Usar nombres de archivo":
//   • apagado → cada item es su propia unidad (key = id del item);
//   • prendido → grupos por stem del filename (groupItemsByStem, key = stem).
// Encima de esa base se aplican los merges manuales (`attachTo`): el usuario
// puede "añadir esta imagen a otro producto" tocándola en la lista, y eso se
// registra como itemId → key de la unidad destino. Los archivos añadidos van
// al FINAL de la unidad destino: la imagen principal (la primera) del
// destino no cambia por recibir imágenes.

import { groupItemsByStem } from "./groupFilesByStem";

export interface BulkUnit<T> {
  /** Estable entre renders: id del item host (modo plano) o key del stem
   *  (modo "usar nombres de archivo"). */
  key: string;
  /** Nombre derivado del filename (solo modo por stem; null en plano, donde
   *  el nombre vive en el propio item host). */
  baseName: string | null;
  /** Imágenes de la unidad. La primera es la principal. */
  files: T[];
}

/**
 * Calcula las unidades a partir de los items, el modo y los merges manuales.
 *
 * Un `attachTo` que apunta a una unidad que ya no existe (el host se borró
 * por un camino que no limpió el mapa) NO tira el archivo: el item vuelve a
 * ser su propia unidad. Perder archivos en silencio es el único resultado
 * prohibido acá.
 */
export function computeUnits<T extends { id: string }>(
  items: T[],
  byStem: boolean,
  getName: (item: T) => string,
  attachTo: Record<string, string>,
): BulkUnit<T>[] {
  const hosts: T[] = [];
  const attached: T[] = [];
  for (const it of items) {
    // hasOwnProperty.call y no Object.hasOwn: la lib del tsconfig es < es2022.
    (Object.prototype.hasOwnProperty.call(attachTo, it.id) ? attached : hosts)
      .push(it);
  }

  const units: BulkUnit<T>[] = byStem
    ? groupItemsByStem(hosts, getName).map((g) => ({
        key: g.key,
        baseName: g.baseName,
        files: [...g.files],
      }))
    : hosts.map((it) => ({ key: it.id, baseName: null, files: [it] }));

  const byKey = new Map(units.map((u) => [u.key, u]));

  const asOwnUnit = (it: T) => {
    if (byStem) {
      // Se reinserta con el mismo algoritmo de la base: si su stem coincide
      // con una unidad existente, cae ahí; si no, unidad nueva.
      const g = groupItemsByStem([it], getName)[0];
      const existing = byKey.get(g.key);
      if (existing) {
        existing.files.push(it);
      } else {
        const unit = { key: g.key, baseName: g.baseName, files: [it] };
        units.push(unit);
        byKey.set(unit.key, unit);
      }
    } else {
      const unit = { key: it.id, baseName: null, files: [it] };
      units.push(unit);
      byKey.set(unit.key, unit);
    }
  };

  for (const it of attached) {
    const target = byKey.get(attachTo[it.id]);
    if (target) target.files.push(it);
    else asOwnUnit(it);
  }

  return units;
}

// ── "Agrupar con IA" ─────────────────────────────────────────────────────

/** Un grupo como lo devuelve el backend (POST /v1/products/group-images):
 *  ids de items del front (el primero es la portada) + nombre sugerido. */
export interface GrupoIaRespuesta {
  ids: string[];
  nombre: string;
}

/**
 * Convierte los grupos de la IA en el mapa `attachTo` (modo plano: host = el
 * primer id del grupo) + nombres sugeridos por host. Cinturón defensivo del
 * lado del cliente, aunque el backend ya sanea: ids desconocidos se ignoran,
 * repetidos gana la primera aparición, y el excedente sobre `maxPorGrupo`
 * queda como unidad propia — nunca se pierde una imagen.
 */
export function attachToDesdeGrupos(
  idsExistentes: string[],
  grupos: GrupoIaRespuesta[],
  maxPorGrupo: number,
): { attachTo: Record<string, string>; nombres: Record<string, string> } {
  const existentes = new Set(idsExistentes);
  const usados = new Set<string>();
  const attachTo: Record<string, string> = {};
  const nombres: Record<string, string> = {};
  for (const g of grupos) {
    const ids = g.ids.filter((id) => existentes.has(id) && !usados.has(id));
    ids.forEach((id) => usados.add(id));
    const efectivos = ids.slice(0, maxPorGrupo);
    const host = efectivos[0];
    if (!host) continue;
    for (const id of efectivos.slice(1)) attachTo[id] = host;
    const nombre = (g.nombre ?? "").trim();
    if (nombre) nombres[host] = nombre;
  }
  return { attachTo, nombres };
}
